import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { NotificationContext } from '../../context/NotificationContext';
import { UserContext } from '../../context/UserContext';
import { getAdminEmails, createAdminEmail, updateAdminEmail, deleteAdminEmail, sendAdminEmail, getZohoStatus, getZohoAuthUrl } from '../../services/adminService';
import './AdminEmails.css';

const AdminEmails = () => {
  const { t } = useTranslation();
  const [emails, setEmails] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [emailToView, setEmailToView] = useState(null);
  const [editingEmailId, setEditingEmailId] = useState(null); // New state for editing
  const [isZohoAuthenticated, setIsZohoAuthenticated] = useState(false);
  const [emailToSendId, setEmailToSendId] = useState(null);
  const [error, setError] = useState(null);

  // Form state
  const [subject, setSubject] = useState('');
  const [generalMessage, setGeneralMessage] = useState('');
  const [providerMessage, setProviderMessage] = useState('');
  const [filesChanged, setFilesChanged] = useState({
    termsOfUse: false,
    privacyPolicy: false,
    cancellationPolicy: false,
    providerTermsOfUse: false,
    cookiePolicy: false,
  });

  const { addNotification } = useContext(NotificationContext);
  const { user } = useContext(UserContext);

  const resetForm = () => {
    setSubject('');
    setGeneralMessage('');
    setProviderMessage('');
    setFilesChanged({
      termsOfUse: false,
      privacyPolicy: false,
      cancellationPolicy: false,
      providerTermsOfUse: false,
      cookiePolicy: false,
    });
    setEditingEmailId(null); // Also exit edit mode
  };

  const fetchEmails = useCallback(async () => {
    if (!user.token) return;
    setIsLoading(true);
    try {
      const fetchedEmails = await getAdminEmails(user.token);
      setEmails(fetchedEmails);
      setError(null);
    } catch (err) {
      setError('Failed to load email history.');
      addNotification(err.message, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [user.token, addNotification]);

  useEffect(() => {
    fetchEmails();
  }, [fetchEmails]);

  useEffect(() => {
    const checkZohoStatus = async () => {
      if (!user.token) return;
      try {
        const { isAuthenticated } = await getZohoStatus(user.token);
        setIsZohoAuthenticated(isAuthenticated);
      } catch (err) {
        addNotification('Could not verify Zoho connection status.', 'error');
      }
    };
    checkZohoStatus();
  }, [user.token, addNotification]);

  const handleSaveDraft = async (e) => {
    e.preventDefault();
    if (!subject.trim()) {
      addNotification('Subject is required.', 'error');
      return;
    }
    if (!generalMessage.trim() && !providerMessage.trim()) {
      addNotification('At least one message body (general or provider) is required.', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const emailData = {
        subject,
        generalMessage,
        providerMessage,
        filesChanged,
      };
      if (editingEmailId) {
        await updateAdminEmail(editingEmailId, emailData, user.token);
        addNotification('Draft updated successfully!', 'success');
      } else {
        await createAdminEmail(emailData, user.token);
        addNotification('Draft saved successfully!', 'success');
      }
      await fetchEmails(); // Refresh the list
      resetForm(); // Clear the form and exit edit mode
    } catch (err) {
      addNotification(err.message, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditClick = (email) => {
    setEditingEmailId(email._id);
    setSubject(email.subject);
    setGeneralMessage(email.generalMessage || '');
    setProviderMessage(email.providerMessage || '');
    setFilesChanged({
      termsOfUse: email.filesChanged?.termsOfUse || false,
      privacyPolicy: email.filesChanged?.privacyPolicy || false,
      cancellationPolicy: email.filesChanged?.cancellationPolicy || false,
      providerTermsOfUse: email.filesChanged?.providerTermsOfUse || false,
      cookiePolicy: email.filesChanged?.cookiePolicy || false,
    });
  };

  const handleCancelEdit = () => {
    resetForm();
  };

  const handleDeleteClick = (emailId) => {
    addNotification(
      'Are you sure you want to delete this draft?',
      'warning',
      0, // duration 0 means it stays until closed
      true, // isConfirmation
      [
        { text: 'Cancel', onClick: () => {} },
        {
          text: 'Yes, Delete',
          onClick: async () => {
            try {
              await deleteAdminEmail(emailId, user.token);
              addNotification('Draft deleted successfully!', 'success');
              fetchEmails(); // Refresh the list
            } catch (err) {
              addNotification(`Failed to delete draft: ${err.message}`, 'error');
            }
          },
          className: 'delete-btn', // Optional: for styling the delete button
        },
      ]
    );
  };

  const handleDuplicate = async (emailToDuplicate) => {
    setIsSubmitting(true);
    try {
      const newDraftData = {
        subject: `Copy of: ${emailToDuplicate.subject}`,
        generalMessage: emailToDuplicate.generalMessage,
        providerMessage: emailToDuplicate.providerMessage,
        filesChanged: emailToDuplicate.filesChanged,
      };

      await createAdminEmail(newDraftData, user.token);
      addNotification('Draft duplicated successfully!', 'success');
      await fetchEmails(); // Refresh list to show the new draft
    } catch (err) {
      addNotification(`Failed to duplicate draft: ${err.message}`, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    // This handler is for the compose form's send button, which should save a draft first.
    // For sending from history, use handleSendFromHistory.
    addNotification('Please save your email as a draft first, then send it from the history table.', 'info');
  };

  const handleTestListCreation = async () => {
    setIsTesting(true);
    try {
      const response = await sendAdminEmail({ onlyCreateLists: true }, user.token);
      addNotification(response.message || 'Test list creation initiated successfully!', 'success');
      // No need to refresh history for a test that doesn't use a draft
    } catch (err) {
      addNotification(err.message, 'error');
    } finally {
      setIsTesting(false);
    }
  };

  const handleViewEmail = (email) => {
    setEmailToView(email);
    setShowViewModal(true);
  };

  const handleAuthenticate = async () => {
    try {
      const { authUrl } = await getZohoAuthUrl(user.token);
      // Open the URL in a new window/tab for the user to authenticate
      window.open(authUrl, '_blank', 'width=600,height=700');
      // Optionally, you can add logic here to poll for the authentication status
      // after the window is closed, or instruct the user to refresh the page.
      addNotification('Please complete the authentication in the new window. You may need to refresh this page afterward.', 'info');
    } catch (err) {
      addNotification(`Failed to get authentication URL: ${err.message}`, 'error');
    }
  };

  const handleSendFromHistory = (emailId) => {
    setEmailToSendId(emailId);
    // setOnlyCreateLists(false); // Reset checkbox state when opening modal
    setShowConfirmationModal(true);
  };

  const handleConfirmSend = async () => {
    setShowConfirmationModal(false);
    if (!emailToSendId) return;

    setIsSubmitting(true);
    try {
      const payload = { id: emailToSendId };
      const response = await sendAdminEmail(payload, user.token);
      addNotification(response.message || 'Email sending initiated successfully!', 'success');
      await fetchEmails(); // Refresh the list
    } catch (err) {
      addNotification(err.message, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFilesChanged((prev) => ({ ...prev, [name]: checked }));
  };

  const formatHistoryDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false, // Use 24-hour format
    });
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div style={{ color: 'red' }}>Error: {error}</div>;
  }

  return (
    <div className="admin-emails-container">
      <h2>Admin Emails</h2>
      <div className="zoho-status-section">
        <h4>Zoho Campaigns Connection</h4>
        {isZohoAuthenticated ? (
          <div className="status-indicator connected">
            <span className="status-icon">✔</span> Connected
            <button onClick={handleTestListCreation} className="test-button" disabled={isTesting}>
              {isTesting ? 'Testing...' : 'Test List Creation'}
            </button>
          </div>
        ) : (
          <div className="status-indicator disconnected">
            <span className="status-icon">✖</span> Disconnected
            <button onClick={handleAuthenticate} className="auth-button">
              Authenticate with Zoho
            </button>
          </div>
        )}
      </div>
      <div className="form-and-history-layout">
        <div className="form-section">
          <h3>{editingEmailId ? 'Edit Draft' : 'Compose New Email'}</h3>
          <form className="email-form" onSubmit={handleSaveDraft}>
            <div className="form-group">
              <label htmlFor="subject">Subject </label>
              <input type="text" id="subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Email Subject" required />
            </div>
            <div className="form-group">
              <label htmlFor="generalMessage">General Message (for all users)</label>
              <textarea id="generalMessage" value={generalMessage} onChange={(e) => setGeneralMessage(e.target.value)} rows="8" placeholder="This message will be sent to all targeted users."></textarea>
            </div>
            <div className="form-group">
              <label htmlFor="providerMessage">Provider-Specific Message (optional)</label>
              <textarea id="providerMessage" value={providerMessage} onChange={(e) => setProviderMessage(e.target.value)} rows="5" placeholder="This message will be appended for Providers only."></textarea>
            </div>

            <fieldset className="form-group">
              <legend>Include Links to Changed Documents (optional)</legend>
              <div className="checkbox-grid">
                {Object.keys(filesChanged).map((key) => (
                  <div className="checkbox-item" key={key}>
                    <input type="checkbox" id={key} name={key} checked={filesChanged[key]} onChange={handleCheckboxChange} />
                    <label htmlFor={key}>{t(`userMenu.${key}`)}</label>
                  </div>
                ))}
              </div>
            </fieldset>

            <div className="form-actions">
              <button type="submit" className="draft-button" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : editingEmailId ? 'Update Draft' : 'Save Draft'}
              </button>
              {editingEmailId && (
                <button type="button" className="cancel-button" onClick={handleCancelEdit} disabled={isSubmitting}>
                  Cancel Edit
                </button>
              )}
            </div>
          </form>
        </div>
        <div className="history-section">
          <h3>Email History</h3>
          {isLoading ? (
            <p>Loading history...</p>
          ) : error ? (
            <p className="error-text">{error}</p>
          ) : emails.length === 0 ? (
            <p>No emails have been created yet.</p>
          ) : (
            <div className="history-table-container">
              <table className="history-table">
                <thead>
                  <tr>
                    <th>Subject</th>
                    <th>Created At</th>
                    <th>Sent At</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {emails.map((email) => (
                    <tr key={email._id}>
                      <td className="subject-cell">{email.subject}</td>
                      <td>{formatHistoryDate(email.createdAt)}</td>
                      <td>{formatHistoryDate(email.sentAt)}</td>
                      <td className="actions-cell">
                        {email.status === 'draft' ? (
                          <>
                            <button className="view-action-button" onClick={() => handleEditClick(email)}>
                              Edit
                            </button>
                            <button className="delete-action-button" onClick={() => handleDeleteClick(email._id)} disabled={isSubmitting}>
                              Delete
                            </button>
                          </>
                        ) : (
                          <button className="view-action-button" onClick={() => handleViewEmail(email)}>
                            View
                          </button>
                        )}
                        <button className="view-action-button" onClick={() => handleDuplicate(email)} disabled={isSubmitting}>
                          Duplicate
                        </button>
                        <button className="send-action-button" onClick={() => handleSendFromHistory(email._id)} disabled={email.status !== 'draft' || isSubmitting}>
                          {email.status === 'draft' ? (isSubmitting && emailToSendId === email._id ? 'Sending...' : 'Send') : 'Sent'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showConfirmationModal && (
        <div className="modal-backdrop">
          <div className="modal-content">
            <h3>Confirm Email Send</h3>
            <p>Are you sure you want to send this email? This action cannot be undone.</p>
            <div className="modal-actions">
              <button className="cancel-button" onClick={() => setShowConfirmationModal(false)} disabled={isSubmitting}>
                Cancel
              </button>
              <button className="confirm-button" onClick={handleConfirmSend} disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Confirm Send'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showViewModal && emailToView && (
        <div className="modal-backdrop">
          <div className="modal-content view-modal-content">
            <h3>{emailToView.subject}</h3>
            <div className="view-modal-body">
              {emailToView.generalMessage && (
                <div className="view-modal-section">
                  <h4>General Message</h4>
                  <p>{emailToView.generalMessage}</p>
                </div>
              )}
              {emailToView.providerMessage && (
                <div className="view-modal-section">
                  <h4>Provider-Specific Message</h4>
                  <p>{emailToView.providerMessage}</p>
                </div>
              )}
              {emailToView.filesChanged && Object.values(emailToView.filesChanged).some((v) => v) && (
                <div className="view-modal-section">
                  <h4>Changed Documents</h4>
                  <ul className="changed-files-list">
                    {Object.entries(emailToView.filesChanged)
                      .filter(([, value]) => value)
                      .map(([key]) => (
                        <li key={key}>{t(`userMenu.${key}`)}</li>
                      ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button className="cancel-button" onClick={() => setShowViewModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEmails;
